import { api, unwrap } from "./api";

export type SignoffStatus =
  | "pending_review"
  | "changes_requested"
  | "accepted"
  | "archived";
export type Urgency = "stale" | "ok";
/** Cumulative "completed within the last …" ranges (Grafana-style, nested). */
export type CompletedAgeBucket = "24h" | "7d" | "30d" | "90d" | "180d";

/** Board filters (URL-driven, applied across all three columns). */
export type BoardFilters = {
  lists: string[];
  assignees: string[];
  creators: string[];
  completedAge: CompletedAgeBucket | null;
  /** Review-priority levels to show (1–3); empty = all. */
  priorities: number[];
  /** Free-text quick search over the title; "" = no search. */
  search: string;
};

/** Distinct values powering the filter dropdowns. */
export type BoardFacets = {
  lists: string[];
  assignees: string[];
  creators: string[];
};

export const EMPTY_FILTERS: BoardFilters = {
  lists: [],
  assignees: [],
  creators: [],
  completedAge: null,
  priorities: [],
  search: "",
};

/** Normalise the optional URL search shape into a full BoardFilters object. */
export function filtersFromSearch(s: {
  lists?: string[];
  assignees?: string[];
  creators?: string[];
  completedAge?: CompletedAgeBucket | null;
  priorities?: number[];
  q?: string;
}): BoardFilters {
  return {
    lists: s.lists ?? [],
    assignees: s.assignees ?? [],
    creators: s.creators ?? [],
    completedAge: s.completedAge ?? null,
    priorities: s.priorities ?? [],
    search: s.q ?? "",
  };
}

/** Strip empty fields so the URL stays clean (no ?lists=[]&completedAge=null noise). */
export function filtersToSearch(f: BoardFilters): {
  lists?: string[];
  assignees?: string[];
  creators?: string[];
  completedAge?: CompletedAgeBucket;
  priorities?: number[];
  q?: string;
} {
  const q = f.search.trim();
  return {
    lists: f.lists.length ? f.lists : undefined,
    assignees: f.assignees.length ? f.assignees : undefined,
    creators: f.creators.length ? f.creators : undefined,
    completedAge: f.completedAge ?? undefined,
    priorities: f.priorities.length ? f.priorities : undefined,
    q: q ? q : undefined,
  };
}
export type ClickUpTag = { name: string; fg: string | null; bg: string | null };
/** One assignee on a task (id = ClickUp user id; rest is for rendering). */
export type TaskAssignee = {
  id: string;
  name: string | null;
  avatar: string | null;
  initials: string | null;
};

export type ClickUpSubtask = {
  id: string;
  name: string;
  status: string | null;
  statusColor: string | null;
  statusType: string | null;
  assigneeName: string | null;
  assigneeInitials: string | null;
  assigneeAvatar: string | null;
  priority: string | null;
  priorityColor: string | null;
  dueDate: string | null;
  tags: ClickUpTag[];
  url: string | null;
};

/** A ClickUp task-level attachment (image rendered inline; otherwise a chip). */
export type ClickUpAttachment = {
  id: string;
  title: string | null;
  mimetype: string | null;
  url: string;
  thumbnail: string | null;
  isImage: boolean;
  width: number | null;
  height: number | null;
};

export type CommentAttachment = {
  url: string | null;
  title: string | null;
  extension: string | null;
  mimetype: string | null;
  thumbnail: string | null;
};

export type CommentFragment = {
  text: string;
  type: string | null;
  link: string | null;
  bold: boolean;
  italic: boolean;
  code: boolean;
  attachment: CommentAttachment | null;
};

/** Rich-text comment document (TipTap/ProseMirror JSON); mirrors the DB shape. */
export type RichDoc = { type: "doc"; content?: unknown[] };

/** A Greenlight-stored attachment on a comment (metadata; bytes served by URL). */
export type AttachmentMeta = {
  id: string;
  filename: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  /** Raster image → rendered inline; otherwise a file chip. */
  isImage: boolean;
  /** Greenlight serve URL. */
  url: string;
  /** ClickUp CDN URL once pushed (preferred when present). */
  clickupUrl: string | null;
};

/** Optional rich body + uploaded-attachment ids sent with a comment/decision. */
export type CommentExtras = { rich?: RichDoc | null; attachmentIds?: string[] };

export type ClickUpComment = {
  id: string;
  text: string;
  fragments: CommentFragment[];
  authorName: string | null;
  authorAvatar: string | null;
  authorInitials: string | null;
  date: string | null;
};

export type BoardTask = {
  id: string;
  title: string | null;
  clickupListName: string | null;
  assignees: TaskAssignee[];
  hasDescription: boolean;
  signoffStatus: SignoffStatus;
  statusChangedAt: string;
  daysInState: number;
  /** Days since the task entered ClickUp's complete state; null if never completed. */
  daysSinceComplete: number | null;
  urgency: Urgency;
  /** Dev/admin review-priority flag: 0 none, 1–3 (! / !! / !!!). */
  reviewPriority: number;
};

export type SyncResult = {
  fetched: number;
  created: number;
  reprojected: number;
  unchanged: number;
};

export type TaskDetailData = {
  task: {
    id: string;
    title: string | null;
    description: string | null;
    clickupListName: string | null;
    clickupCreatorName: string | null;
    clickupCreatorAvatar: string | null;
    clickupCreatorInitials: string | null;
    clickupAssignees: TaskAssignee[];
    clickupUrl: string | null;
    clickupStatus: string | null;
    clickupStatusColor: string | null;
    clickupPriority: string | null;
    clickupPriorityColor: string | null;
    clickupTags: ClickUpTag[] | null;
    clickupStartDate: string | null;
    clickupDueDate: string | null;
    clickupDateCreated: string | null;
    signoffStatus: SignoffStatus;
    statusChangedAt: string | null;
    enteredCompleteAt: string | null;
    reviewPriority: number;
    reviewerName: string | null;
    decidedAt: string | null;
  };
  history: {
    id: string;
    action: string;
    fromStatus: string | null;
    toStatus: string | null;
    createdAt: string;
    actorName: string | null;
  }[];
  comments: {
    id: string;
    body: string;
    rich: RichDoc | null; // rich-text doc; null = plain-text (body) only
    kind: string; // "comment" (general) | "feedback" (sign-off rationale)
    parentId: string | null; // reply target (a comment id), or null for top-level
    createdAt: string;
    editedAt: string | null; // set when the author edited the body
    authorId: string; // for gating "edit your own comment"
    authorName: string | null;
    authorAvatar: string | null;
    pushedToClickup: boolean;
    attachments: AttachmentMeta[]; // Greenlight-stored files on this comment
  }[];
  clickupSubtasks: ClickUpSubtask[];
  clickupComments: ClickUpComment[];
  /** Rich markdown description from ClickUp (lists/links preserved); may be null. */
  clickupMarkdown: string | null;
  /**
   * ClickUp's task-level attachments (the Attachments panel). Optional: a
   * persisted cache snapshot from before this field existed rehydrates without
   * it, so consumers must tolerate `undefined`.
   */
  clickupAttachments?: ClickUpAttachment[];
};

export async function fetchTaskDetail(id: string): Promise<TaskDetailData> {
  return unwrap(api.api.tasks({ id }).get());
}

// One column's page of board tasks (TanStack Query owns caching now).
export type BoardPage = { tasks: BoardTask[]; total: number };

export async function fetchBoardPage(
  status: SignoffStatus,
  limit: number,
  offset: number,
  spaceId: string,
  filters: BoardFilters = EMPTY_FILTERS,
): Promise<BoardPage> {
  const query: Record<string, string> = {
    status,
    space_id: spaceId,
    limit: String(limit),
    offset: String(offset),
  };
  if (filters.lists.length) query.lists = filters.lists.join(",");
  if (filters.assignees.length) query.assignees = filters.assignees.join(",");
  if (filters.creators.length) query.creators = filters.creators.join(",");
  if (filters.completedAge) query.completedAge = filters.completedAge;
  if (filters.priorities.length)
    query.priorities = filters.priorities.join(",");
  if (filters.search.trim()) query.q = filters.search.trim();
  return unwrap(api.api.board.get({ query }));
}

/** Distinct lists/assignees/creators for the given space's live tasks. */
export async function fetchBoardFacets(spaceId: string): Promise<BoardFacets> {
  return unwrap(api.api.board.facets.get({ query: { space_id: spaceId } }));
}

// ---- Archive ----------------------------------------------------------------

export type ArchivedTask = {
  id: string;
  title: string | null;
  clickupListName: string | null;
  assignees: TaskAssignee[];
  archiveReason: string | null;
  enteredCompleteAt: string | null;
  statusChangedAt: string | null;
};
export type ArchivedPage = { tasks: ArchivedTask[]; total: number };

/** One page of archived tasks for the given space (optional title search). */
export async function fetchArchived(
  limit: number,
  offset: number,
  search: string,
  spaceId: string,
): Promise<ArchivedPage> {
  const query: Record<string, string> = {
    space_id: spaceId,
    limit: String(limit),
    offset: String(offset),
  };
  if (search.trim()) query.q = search.trim();
  return unwrap(api.api.archived.get({ query }));
}

/** Manually archive a single task (card ⋯ menu). */
export async function archiveTask(id: string): Promise<{ ok: boolean }> {
  return unwrap(api.api.tasks({ id }).archive.post());
}

/** Manually un-archive a task → back to Pending review (cutoff-exempt). */
export async function unarchiveTask(id: string): Promise<{ ok: boolean }> {
  return unwrap(api.api.tasks({ id }).unarchive.post());
}

export type DecisionResult = {
  ok: true;
  signoffStatus: "accepted" | "changes_requested";
};

/** Accept a task (sign-off). Mutates Greenlight state + posts the sign-off to ClickUp. */
export async function acceptTask(id: string): Promise<DecisionResult> {
  return unwrap(api.api.tasks({ id }).accept.post());
}

/** Send a task back with feedback (+ optional rich body / attachments). */
export async function requestChanges(
  id: string,
  feedback: string,
  extras: CommentExtras = {},
): Promise<DecisionResult> {
  return unwrap(
    api.api.tasks({ id })["request-changes"].post({ feedback, ...extras }),
  );
}

/** A subtask's markdown description, fetched lazily when it's expanded. */
export async function fetchSubtaskDescription(
  clickupId: string,
): Promise<string | null> {
  const { markdown } = await unwrap(
    api.api.clickup.description.get({ query: { id: clickupId } }),
  );
  return markdown;
}

/** Undo a decision — move a decided task back to Pending review. */
export async function reopenTask(
  id: string,
): Promise<{ ok: true }> {
  return unwrap(api.api.tasks({ id }).reopen.post());
}

/** Set a task's review-priority flag (0–3). admin/developer only. */
export async function setReviewPriority(
  id: string,
  level: number,
): Promise<{ ok: boolean; reviewPriority: number }> {
  return unwrap(api.api.tasks({ id }).priority.patch({ level }));
}

/** Add a Greenlight comment (or a reply via parentId), + optional rich/attachments. */
export async function addTaskComment(
  id: string,
  body: string,
  parentId?: string | null,
  extras: CommentExtras = {},
): Promise<{ ok: true }> {
  return unwrap(
    api.api
      .tasks({ id })
      .comment.post({ body, parentId: parentId ?? null, ...extras }),
  );
}

/** Edit one's own Greenlight comment (general, reply, or kickback feedback). */
export async function editTaskComment(
  taskId: string,
  commentId: string,
  body: string,
  extras: CommentExtras = {},
): Promise<{ ok: true }> {
  return unwrap(
    api.api.tasks({ id: taskId }).comment({ commentId }).patch({ body, ...extras }),
  );
}

/** Admin-only: pull fresh `complete` tasks from ClickUp into the projection. */
export async function triggerSync(): Promise<SyncResult> {
  return unwrap(api.api.sync.post());
}
